import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackDashboardPropertyFilterChange } from '@/lib/analytics/events';
import type { AssetType, PropertyListFilters, UnitAvailabilityStatus } from '@/lib/types/property';

export interface RangeValue {
  min: number | null;
  max: number | null;
}

export interface DashboardPropertyFiltersState {
  submarkets: string[];
  assetTypes: AssetType[];
  rentableSquareFeet: RangeValue;
  rentPerSquareFoot: RangeValue;
  readinessStates: UnitAvailabilityStatus[];
  incentivesOnly: boolean;
  potentiallyDivisible: boolean | null;
  hasVacancy: boolean;
}

export type DashboardPropertyFilterUpdater =
  | DashboardPropertyFiltersState
  | ((current: DashboardPropertyFiltersState) => DashboardPropertyFiltersState);

const EMPTY_RANGE: RangeValue = { min: null, max: null };

function createInitialFilterState(): DashboardPropertyFiltersState {
  return {
    submarkets: [],
    assetTypes: [],
    rentableSquareFeet: { ...EMPTY_RANGE },
    rentPerSquareFoot: { ...EMPTY_RANGE },
    readinessStates: [],
    incentivesOnly: false,
    potentiallyDivisible: null,
    hasVacancy: false,
  };
}

function normalizeRange(range?: RangeValue): RangeValue {
  if (!range) {
    return { ...EMPTY_RANGE };
  }
  const normalized: RangeValue = {
    min: typeof range.min === 'number' ? range.min : null,
    max: typeof range.max === 'number' ? range.max : null,
  };
  return normalized;
}

function normalizeState(state: DashboardPropertyFiltersState): DashboardPropertyFiltersState {
  return {
    submarkets: Array.isArray(state.submarkets) ? [...state.submarkets] : [],
    assetTypes: Array.isArray(state.assetTypes) ? [...state.assetTypes] : [],
    rentableSquareFeet: normalizeRange(state.rentableSquareFeet),
    rentPerSquareFoot: normalizeRange(state.rentPerSquareFoot),
    readinessStates: Array.isArray(state.readinessStates) ? [...state.readinessStates] : [],
    incentivesOnly: state.incentivesOnly,
    potentiallyDivisible:
      typeof state.potentiallyDivisible === 'boolean' ? state.potentiallyDivisible : null,
    hasVacancy: state.hasVacancy,
  };
}

function buildPropertyListFilters(state: DashboardPropertyFiltersState): PropertyListFilters {
  const filters: PropertyListFilters = {};

  if (state.submarkets.length > 0) {
    filters.submarkets = state.submarkets;
  }

  if (state.assetTypes.length > 0) {
    filters.assetTypes = state.assetTypes;
  }

  if (hasRangeValue(state.rentableSquareFeet)) {
    filters.rentableSquareFeet = state.rentableSquareFeet;
  }

  if (hasRangeValue(state.rentPerSquareFoot)) {
    filters.rentPerSquareFoot = state.rentPerSquareFoot;
  }

  if (state.readinessStates.length > 0) {
    filters.readiness = state.readinessStates;
  }

  if (state.incentivesOnly) {
    filters.incentivesOnly = true;
  }

  if (typeof state.potentiallyDivisible === 'boolean') {
    filters.potentiallyDivisible = state.potentiallyDivisible;
  }

  if (state.hasVacancy) {
    filters.hasVacancy = true;
  }

  return filters;
}

function hasRangeValue(range: RangeValue): boolean {
  return range.min != null || range.max != null;
}

function countActiveFilters(state: DashboardPropertyFiltersState): number {
  let count = 0;
  if (state.submarkets.length > 0) count += 1;
  if (state.assetTypes.length > 0) count += 1;
  if (hasRangeValue(state.rentableSquareFeet)) count += 1;
  if (hasRangeValue(state.rentPerSquareFoot)) count += 1;
  if (state.readinessStates.length > 0) count += 1;
  if (state.incentivesOnly) count += 1;
  if (typeof state.potentiallyDivisible === 'boolean') count += 1;
  if (state.hasVacancy) count += 1;
  return count;
}

export interface UseDashboardPropertyFiltersResult {
  filters: DashboardPropertyFiltersState;
  appliedFilters: PropertyListFilters;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  filtersHash: string;
  updateFilters: (updater: DashboardPropertyFilterUpdater) => void;
  setFilterValue: <K extends keyof DashboardPropertyFiltersState>(
    key: K,
    value: DashboardPropertyFiltersState[K],
  ) => void;
  resetFilters: () => void;
}

export function useDashboardPropertyFilters(): UseDashboardPropertyFiltersResult {
  const [state, setState] = useState<DashboardPropertyFiltersState>(createInitialFilterState);

  const appliedFilters = useMemo(() => buildPropertyListFilters(state), [state]);
  const activeFilterCount = useMemo(() => countActiveFilters(state), [state]);
  const hasActiveFilters = activeFilterCount > 0;
  const filtersHash = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

  const updateFilters = useCallback((updater: DashboardPropertyFilterUpdater) => {
    setState((current) => {
      const next =
        typeof updater === 'function'
          ? (updater as (value: DashboardPropertyFiltersState) => DashboardPropertyFiltersState)(
              current,
            )
          : updater;
      return normalizeState(next);
    });
  }, []);

  const setFilterValue = useCallback(
    <K extends keyof DashboardPropertyFiltersState>(
      key: K,
      value: DashboardPropertyFiltersState[K],
    ) => {
      updateFilters((current) => ({
        ...current,
        [key]: value,
      }));
    },
    [updateFilters],
  );

  const resetFilters = useCallback(() => {
    setState(createInitialFilterState());
  }, []);

  const prevHashRef = useRef(filtersHash);
  useEffect(() => {
    if (prevHashRef.current === filtersHash) {
      return;
    }
    prevHashRef.current = filtersHash;
    trackDashboardPropertyFilterChange(appliedFilters);
  }, [appliedFilters, filtersHash]);

  return {
    filters: state,
    appliedFilters,
    activeFilterCount,
    hasActiveFilters,
    filtersHash,
    updateFilters,
    setFilterValue,
    resetFilters,
  };
}
